import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import type { PageReviewIssue, ReviewIssueLocation } from '../services/pageReviewFeedbackService';

import { snapshotUrl } from './AiOverviewPanel/ReviewSnapshots';
import './ReviewIssueLocation.css';

type ScreenLocation = Extract<ReviewIssueLocation, { kind: 'screen' }>;
type CodeLocation = Extract<ReviewIssueLocation, { kind: 'code' }>;

/** "desktop 1440x900 - section Hero - shot hero" or "frontend/src/pages/Landing.jsx:42". */
export function locationLabel(located: ReviewIssueLocation): string {
  if (located.kind === 'screen') return located.screen;

  return located.line ? `${located.file}:${located.line}` : located.file;
}

const percent = (fraction: number): string => `${Math.round(fraction * 1000) / 10}%`;

/** The screenshot the review saw the issue on, the part it named boxed. */
function ScreenView({ located }: { located: ScreenLocation }) {
  const [x0, y0, x1, y1] = located.box ?? [0, 0, 0, 0];
  const box = { left: percent(x0), top: percent(y0), width: percent(x1 - x0), height: percent(y1 - y0) };

  return (
    <div className="issue-location__screen">
      <img src={snapshotUrl(located.snapshot)} alt={located.screen} />
      {located.box && <span className="issue-location__box" style={box} data-testid="issue-location-box" />}
    </div>
  );
}

/** The lines of code around the one the issue is about, that line marked. */
function CodeView({ located }: { located: CodeLocation }) {
  const lines = located.snippet?.lines ?? [];
  const start = located.snippet?.start ?? 1;

  if (!lines.length) {
    return <p className="issue-location__note">Found in {located.file}; the exact line could not be located.</p>;
  }

  return (
    <pre className="issue-location__code">
      {lines.map((text, offset) => (
        <code
          key={start + offset}
          className={start + offset === located.line ? 'is-hit' : undefined}
          data-testid={start + offset === located.line ? 'issue-location-line' : undefined}
        >
          <span className="issue-location__number" aria-hidden="true">{start + offset}</span>
          {text || ' '}
        </code>
      ))}
    </pre>
  );
}

/** Where a review issue was found, inline: its screenshot with the part boxed, or its code with the line marked. */
export function IssueLocationView({ located }: { located: ReviewIssueLocation }) {
  return located.kind === 'screen' ? <ScreenView located={located} /> : <CodeView located={located} />;
}

/** Where the review found an issue, over the page: its screenshot or its code. Escape or a click outside closes it. */
export const IssueLocationModal: React.FC<{ issue: PageReviewIssue; onClose: () => void }> = ({ issue, onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const located = issue.located;

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!located) return null;

  // Portalled to the page body: the design panels clip and transform their content, which would trap a fixed overlay.
  return createPortal(
    <div
      className="chat-media-lightbox issue-location"
      role="dialog"
      aria-modal="true"
      aria-label={`Where it was found: ${issue.plain || issue.text}`}
      onClick={onClose}
    >
      <figure className="issue-location__figure" onClick={(event) => event.stopPropagation()}>
        <IssueLocationView located={located} />
        <figcaption>
          <strong>{issue.plain || issue.text}</strong>
          <span>{locationLabel(located)}</span>
        </figcaption>
      </figure>
      <button ref={closeRef} type="button" className="chat-media-lightbox__close" aria-label="Close" onClick={onClose}>
        &times;
      </button>
    </div>,
    document.body,
  );
};
